import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Divider, FormControlLabel, Switch, TextField, Typography } from '@mui/material';

/**
 * Leads on Intake home (C14 + OL5, Lloyd 4 Nov 2026), on Settings -> Automation.
 *
 * Its own endpoint (`/api/v1/intake-leads/settings`) so it does not touch the main automation
 * route. Both switches ship off. Nothing here sends anything: a lead is a row on Intake home.
 */
interface LeadSettings {
  leadEmails: boolean;
  unpaidCheckoutLeads: boolean;
  leadsSince: string;
  retentionDays: string;
  leadMailbox: string | null;
  canManage: boolean;
}

const ENDPOINT = (process.env.API_HOST || '') + '/api/v1/intake-leads/settings';

const IntakeLeadSettingsSection: React.FC = () => {
  const [settings, setSettings] = useState<LeadSettings | null>(null);
  const [since, setSince] = useState('');
  const [retention, setRetention] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await fetch(ENDPOINT, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to load the lead settings');
      const body = (await response.json()) as LeadSettings;
      setSettings(body);
      setSince(body.leadsSince);
      setRetention(body.retentionDays);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the lead settings');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (patch: Partial<Record<keyof LeadSettings, unknown>>) => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || body.errors?.[0]?.msg || 'Failed to save the lead settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the lead settings');
    } finally {
      await load();
      setSaving(false);
    }
  };

  if (!settings) {
    return error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={20} />;
  }

  const disabled = !settings.canManage || saving;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h6">Leads on Intake home</Typography>

      {error && <Alert severity="error">{error}</Alert>}

      <FormControlLabel
        control={
          <Switch
            checked={settings.leadEmails}
            onChange={(e) => void save({ leadEmails: e.target.checked })}
            disabled={disabled}
          />
        }
        label="Show LRIS referral and website-form emails as leads"
      />
      <Typography variant="body2" color="text.secondary">
        {settings.leadEmails
          ? `Emails from the State Bar LRIS and the txticketdr.com contact form in ${settings.leadMailbox ?? 'the lead mailbox'} appear on Intake home. The mailbox is only read: nothing is marked read, moved or deleted, and nothing is sent.`
          : 'Off. The mailbox is not read.'}
      </Typography>
      {!settings.leadMailbox && (
        <Alert severity="warning">
          Not configured: no lead mailbox is set on the server (LEAD_EMAIL_MAILBOX), so nothing is read
          even when this is on.
        </Alert>
      )}

      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Switch
            checked={settings.unpaidCheckoutLeads}
            onChange={(e) => void save({ unpaidCheckoutLeads: e.target.checked })}
            disabled={disabled}
          />
        }
        label="Show unpaid website sign-ups as leads"
      />
      <Typography variant="body2" color="text.secondary">
        {settings.unpaidCheckoutLeads
          ? 'Someone who filled in the website sign-up but did not pay appears on Intake home once their quote expires. A sign-up Stripe shows as paid with no case opened appears as "Paid online, case not opened". No client or case is created.'
          : 'Off. Unfinished sign-ups are not shown.'}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        <TextField
          size="small"
          label="Start from (Texas date)"
          placeholder="YYYY-MM-DD"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          onBlur={() => since.trim() !== settings.leadsSince && void save({ leadsSince: since.trim() })}
          disabled={disabled}
          helperText="Blank: from when each switch is turned on"
        />
        <TextField
          size="small"
          label="Clear personal details after (days)"
          value={retention}
          onChange={(e) => setRetention(e.target.value.replace(/\D/g, ''))}
          onBlur={() => retention.trim() !== settings.retentionDays && void save({ retentionDays: retention.trim() })}
          disabled={disabled}
          helperText="Blank: kept until decided"
        />
      </Box>
    </Box>
  );
};

export default IntakeLeadSettingsSection;
